import { useEffect, type ChangeEvent } from "react";
import Redo from "@mui/icons-material/Redo";
import Undo from "@mui/icons-material/Undo";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { useAppDialog } from "@pepsico/layout";
import { usePagePrefs } from "@pepsico/pref-permissions";
import { useIsMutating, useQueryClient } from "@tanstack/react-query";
import { noop } from "es-toolkit";

import { useGetGrowersGeoByLocation } from "api/growers-geo/growers-geo";
import type { GrowerGeoResponse, SiteGeoResponse } from "api/model";
import {
  getGetSitesGeoByLocationQueryKey,
  useGetSitesGeoByLocation,
  useUpdateSiteGeoBoundary,
} from "api/sites-geo/sites-geo";
import { isPlotMapLayerVisible } from "components/PlotMap/utils/layerPrefs";
import { usePlotMap } from "context/PlotMap";
import {
  doesBoundaryOverlapAny,
  fromBoundaryMultiPolygon,
  hasBoundaryGeoJson,
  isBoundarySelfIntersecting,
  toBoundaryMultiPolygon,
} from "context/PlotMap/boundaryUtils";
import { getApiErrorMessage } from "utils";

const getUpdateSiteBoundaryError = getApiErrorMessage(
  "Failed to update site boundary.",
);

interface SiteBoundaryEditorProps {}

const isEditableKeyboardTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const tagName = target.tagName;
  return (
    tagName === "INPUT" ||
    tagName === "TEXTAREA" ||
    tagName === "SELECT" ||
    target.isContentEditable
  );
};

const SiteBoundaryEditor: React.FC<SiteBoundaryEditorProps> = () => {
  const { setAppDialog } = useAppDialog();
  const queryClient = useQueryClient();
  const [prefs] = usePagePrefs();
  const {
    boundary: {
      acceptSaved,
      canRedo,
      canUndo,
      cancelDraft,
      clear,
      editingSiteGeoId,
      isClosed,
      isDirty,
      isSiteBoundaryMode,
      label: boundaryLabel,
      points,
      redo,
      setLabel: setBoundaryLabel,
      undo,
    },
  } = usePlotMap();

  const locationUuid = (prefs.location as string | null | undefined) ?? "";
  const { data: growers = [] } = useGetGrowersGeoByLocation(
    { location_uuid: locationUuid },
    {
      query: {
        enabled: locationUuid !== "",
        select: (res): GrowerGeoResponse[] =>
          res.status === 200 ? res.data : [],
      },
    },
  );
  const { data: sites = [] } = useGetSitesGeoByLocation(undefined, {
    query: {
      select: (res): SiteGeoResponse[] => (res.status === 200 ? res.data : []),
    },
  });
  const { isPending: isUpdating, mutateAsync: updateSiteGeo } =
    useUpdateSiteGeoBoundary();
  const isDeleting = useIsMutating({ mutationKey: ["deleteSiteGeo"] }) > 0;
  const isSaving = isDeleting || isUpdating;

  const editingSite =
    editingSiteGeoId == null
      ? null
      : (sites.find((site) => site.id === editingSiteGeoId) ?? null);
  const linkedGrower =
    editingSite?.grower_id == null
      ? null
      : (growers.find((grower) => grower.id === editingSite.grower_id) ?? null);
  const linkedGrowerLabel =
    linkedGrower?.grower_label?.trim() || "Unlabelled Grower Boundary";
  const hasBoundaryLabel = boundaryLabel.trim() !== "";
  const isSelfIntersecting =
    isClosed && points.length >= 3 && isBoundarySelfIntersecting(points);
  const locationGrowerIds = new Set(growers.map((grower) => grower.id));
  const otherSiteRings = sites.flatMap((site) => {
    if (
      site.id === editingSiteGeoId ||
      site.grower_id == null ||
      !locationGrowerIds.has(site.grower_id) ||
      site.boundary == null ||
      !hasBoundaryGeoJson(site.boundary)
    ) {
      return [];
    }
    const ring = fromBoundaryMultiPolygon(site.boundary);
    return ring == null ? [] : [ring];
  });
  const isOverlappingOtherSite =
    isClosed &&
    points.length >= 3 &&
    doesBoundaryOverlapAny(points, otherSiteRings);
  const canSaveBoundary =
    isDirty &&
    isClosed &&
    hasBoundaryLabel &&
    !isSaving &&
    !isSelfIntersecting &&
    !isOverlappingOtherSite;
  const isSiteLayerHidden = !isPlotMapLayerVisible(prefs, "site");

  useEffect(() => {
    if (!isSiteBoundaryMode) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isEditableKeyboardTarget(event.target) || isSaving) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        cancelDraft();
        return;
      }

      const hasModifier = event.metaKey || event.ctrlKey;
      if (!hasModifier || event.key.toLowerCase() !== "z") {
        return;
      }

      if (event.shiftKey) {
        if (!canRedo) {
          return;
        }
        event.preventDefault();
        redo();
        return;
      }

      if (!canUndo) {
        return;
      }
      event.preventDefault();
      undo();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [canRedo, canUndo, cancelDraft, isSaving, isSiteBoundaryMode, redo, undo]);

  const handleBoundaryLabelChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBoundaryLabel(event.target.value);
  };

  const showSaveErrorDialog = (message: string) => {
    setAppDialog({
      confirmText: "Close",
      message: <>{message}</>,
      onConfirm: noop,
      open: true,
      title: "Error",
    });
  };

  const handleSave = async () => {
    if (
      editingSiteGeoId == null ||
      !isDirty ||
      !isClosed ||
      !hasBoundaryLabel ||
      isSaving ||
      isSelfIntersecting ||
      isOverlappingOtherSite
    ) {
      return;
    }

    const boundary = toBoundaryMultiPolygon(points);
    if (boundary == null) {
      showSaveErrorDialog("Boundary must be a closed shape before saving.");
      return;
    }

    const label = boundaryLabel.trim();

    try {
      const response = await updateSiteGeo({
        data: {
          boundary,
          site_label: label,
        },
        siteGeoId: editingSiteGeoId,
      });
      if (response.status !== 200) {
        showSaveErrorDialog(getUpdateSiteBoundaryError(response.data));
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: getGetSitesGeoByLocationQueryKey(),
      });
      acceptSaved();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to save site boundary.";
      showSaveErrorDialog(message);
    }
  };

  return (
    <Stack spacing={1.5}>
      {isSiteLayerHidden && (
        <Alert role="alert" severity="warning">
          Site Boundaries layer is hidden. Show it to see the boundary you're
          editing on the map.
        </Alert>
      )}
      <Typography color="text.secondary" variant="body2">
        Constrained to {linkedGrowerLabel}.
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {isClosed
          ? "Drag vertices to reshape the boundary, or click an edge to insert a point. Press ESC to cancel."
          : "Click the map to place vertices around the site. Click the initial point to close the shape. Press ESC to cancel."}
      </Typography>
      {isSelfIntersecting && (
        <Typography color="error" role="alert" variant="body2">
          Boundary cannot cross itself.
        </Typography>
      )}
      {isOverlappingOtherSite && (
        <Typography color="error" role="alert" variant="body2">
          Boundary cannot overlap another site.
        </Typography>
      )}
      <TextField
        aria-label="Site boundary label"
        disabled={isSaving}
        error={!hasBoundaryLabel}
        fullWidth
        helperText={hasBoundaryLabel ? undefined : "Boundary label is required"}
        label="Boundary label"
        onChange={handleBoundaryLabelChange}
        required
        size="small"
        value={boundaryLabel}
      />
      <Stack direction="row" spacing={1}>
        <Button
          aria-label="Undo site boundary edit"
          disabled={!canUndo || isSaving}
          onClick={undo}
          size="small"
          startIcon={<Undo />}
          variant="text"
        >
          Undo
        </Button>
        <Button
          aria-label="Redo site boundary edit"
          disabled={!canRedo || isSaving}
          onClick={redo}
          size="small"
          startIcon={<Redo />}
          variant="text"
        >
          Redo
        </Button>
        <Button
          aria-label="Clear site boundary"
          disabled={points.length === 0 || isSaving}
          onClick={clear}
          size="small"
          variant="text"
        >
          Clear
        </Button>
        <Button
          aria-label="Cancel site boundary edit"
          color="error"
          disabled={isSaving}
          onClick={cancelDraft}
          size="small"
          variant="text"
        >
          Cancel
        </Button>
        <Button
          aria-label="Save site boundary"
          disabled={!canSaveBoundary}
          loading={isSaving}
          onClick={() => {
            void handleSave();
          }}
          size="small"
          variant="text"
        >
          {isSaving ? "Saving…" : "Save"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default SiteBoundaryEditor;
